import { useMutation, useReactiveVar } from '@apollo/client';
import { AnalyticIdentifier, createError, DocumentNotParsableErrorSchema, getExtendedErrors } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import {
  FileDisplaySection,
  FileDropZone,
  FileMimeType,
  InputFormContainer,
  Stack,
  LabelWithHelp,
  ToastDurationOptions,
} from '@ui-components/neo.tax';
import { useMemo, useState } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { RdProject } from '@neo.tax/rd/hooks';
import {
  AddRdProjectSupportingDocumentMutation,
  AddRdProjectSupportingDocumentMutationVariables,
  ADD_RD_PROJECT_SUPPORTING_DOCUMENT,
  FileUpload,
  GetRdProjectSupportingDocumentSignedUploadUrlMutation,
  GetRdProjectSupportingDocumentSignedUploadUrlMutationVariables,
  GET_RD_PROJECT_SUPPORTING_DOCUMENT_SIGNED_UPLOAD_URL,
  ProcessingProjectState,
  REMOVE_RD_PROJECT_SUPPORTING_DOCUMENT,
  GenerateRdDetailsMutation,
  GenerateRdDetailsMutationVariables,
  GENERATE_RD_DETAILS,
  RemoveRdProjectSupportingDocumentMutationVariables,
  RemoveRdProjectSupportingDocumentMutation,
  initiateResumableFileUpload,
  FileUploadsState,
  ToastConfigState,
  useAnalyticId,
  ResumableFileUpload,
  HELP_UPLOAD_RD_PROJECT_DOCUMENTS_URL,
} from '@neo.tax/shared';

import { GenerateNarrativeModal } from './GenerateNarrativeModal';
import {
  SupportingDocumentsInputFormContainer,
  SupportingDocumentsButton,
} from './rd-project-document-upload-form.styles';

const RD_PROJECT_DOCUMENT_UPLOAD_TAG = 'RD_PROJECT_DOCUMENT_UPLOAD_TAG';
const GENERATE_RD_DETAILS_REQUEST_KEY = 'GENERATE_RD_DETAILS_REQUEST_KEY';

type FileName = string;

export const RdProjectDocumentUploadForm = ({ rdProject }: { rdProject: RdProject }): React.ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { fiscalYearId } = useRdContext();

  const rdProjectSupportingDocumentUploadTag = `${RD_PROJECT_DOCUMENT_UPLOAD_TAG}-${rdProject.id}`;

  const rdProjectSupportingDocumentAnalyticId = useAnalyticId(AnalyticIdentifier.RD_PROJECT_SUPPORTING_DOCUMENT_UPLOAD);
  const rdProjectRemoveSupportingDocumentAnalyticId = useAnalyticId(
    AnalyticIdentifier.RD_PROJECT_REMOVE_SUPPORTING_DOCUMENT,
  );

  useReactiveVar(FileUploadsState.fileUploadsVar); // subscribe re-render on file upload state change

  const [getUploadUrlMutation] = useMutation<
    GetRdProjectSupportingDocumentSignedUploadUrlMutation,
    GetRdProjectSupportingDocumentSignedUploadUrlMutationVariables
  >(GET_RD_PROJECT_SUPPORTING_DOCUMENT_SIGNED_UPLOAD_URL);

  const [addRdProjectSupportingDocument, { loading: loadingAddSupportingDocument }] = useMutation<
    AddRdProjectSupportingDocumentMutation,
    AddRdProjectSupportingDocumentMutationVariables
  >(ADD_RD_PROJECT_SUPPORTING_DOCUMENT);

  const [removeRdProjectSupportingDocument] = useMutation<
    RemoveRdProjectSupportingDocumentMutation,
    RemoveRdProjectSupportingDocumentMutationVariables
  >(REMOVE_RD_PROJECT_SUPPORTING_DOCUMENT);

  const [generateRdDetails] = useMutation<GenerateRdDetailsMutation, GenerateRdDetailsMutationVariables>(
    GENERATE_RD_DETAILS,
    {
      onError: (error) => {
        try {
          const extendedErrors = getExtendedErrors(error, DocumentNotParsableErrorSchema);
          const errorMessage = extendedErrors.map((error) => error.description).join(', ');
          ToastConfigState.pushErrorToast(errorMessage, {
            duration: ToastDurationOptions.LONG,
          });
        } catch {
          Sentry.addBreadcrumb({ level: 'error', data: { error } });
          Sentry.captureException(createError('GenerateRdDetailsError', 'Failed to generate project narrative.'));
          ToastConfigState.pushErrorToast('Failed to generate project narrative. Please try again.', {
            duration: ToastDurationOptions.LONG,
          });
        }
      },
      context: {
        serializationKey: GENERATE_RD_DETAILS_REQUEST_KEY,
        debounceKey: GENERATE_RD_DETAILS_REQUEST_KEY,
        successToast: {
          title: 'Project Narrative generated successfully.',
          description: '',
          duration: 3000,
        },
      },
    },
  );

  const triggerClientFileUpload = async (fileUpload: ResumableFileUpload): Promise<void> => {
    await initiateResumableFileUpload({
      fileUpload,
      getUploadUrl: async () => {
        // get a signed url from our backend to upload the file to
        const uploadUrlResult = await getUploadUrlMutation({
          variables: { fileName: fileUpload.file.name, rdProjectId: rdProject.id },
        });
        const { permanentUrl, signedUrl } = uploadUrlResult.data?.getRdProjectSupportingDocumentSignedUploadUrl ?? {};
        if (!signedUrl || !permanentUrl || uploadUrlResult.errors) {
          Sentry.addBreadcrumb({ level: 'error', data: { uploadUrlResult } });
          throw createError('FileUploadError', 'An unexpected error happened while fetching the signed url.');
        }
        return { permanentUrl, signedUrl };
      },
      onFinishedUploading: async (permanentUrl) => {
        await addRdProjectSupportingDocument({
          variables: {
            rdProjectId: rdProject.id,
            input: { permanentUrl },
          },
          optimisticResponse: {
            addRdProjectSupportingDocument: {
              ...rdProject,
              supportingDocuments: [
                ...rdProject.supportingDocuments,
                {
                  __typename: 'FileUpload',
                  id: 'temp-id',
                  createdAt: new Date().toISOString(),
                  fileName: fileUpload.file.name,
                  signedUrl: 'temp-url',
                },
              ],
            } satisfies RdProject,
          },
        });
      },
    });
  };

  const addFiles = async (files: File[]): Promise<void> => {
    await FileUploadsState.addManyFileUploads(files, rdProjectSupportingDocumentUploadTag);
  };

  const uploadAllFiles = async (): Promise<void> => {
    const fileUploads = FileUploadsState.getFileUploads(rdProjectSupportingDocumentUploadTag);
    await Promise.all(fileUploads.map(triggerClientFileUpload));
  };

  const uploadedFiles = useMemo(
    () =>
      rdProject.supportingDocuments.reduce((acc, file) => {
        acc.set(file.fileName, file);
        return acc;
      }, new Map<FileName, FileUpload>()),
    [rdProject],
  );

  const removeUploadedFile = async (fileName: FileName): Promise<void> => {
    const file = uploadedFiles.get(fileName);
    if (!file) {
      throw createError('RemoveProjectFileError', 'File not found');
    }

    try {
      await removeRdProjectSupportingDocument({
        variables: {
          fileUploadId: file.id,
        },
        optimisticResponse: {
          removeRdProjectSupportingDocument: {
            ...rdProject,
            supportingDocuments: rdProject.supportingDocuments.filter((f) => f.id !== file.id),
          } satisfies RdProject,
        },
      });

      ToastConfigState.pushSuccessToast('Successfully removed file.', {
        duration: 'MEDIUM',
      });
    } catch (error) {
      ToastConfigState.pushErrorToast('Failed To Remove File. Please try again.', {
        duration: 'LONG',
      });

      Sentry.addBreadcrumb({ level: 'error', data: { error } });
      Sentry.captureException(createError('FileUploadError', 'Failed to remove file'));
      console.error(error);
    }
  };

  const handleOpenUpdateNarrativeModal = (): void => {
    setIsModalOpen(true);
  };

  const handleConfirmUpdateNarrative = async (): Promise<void> => {
    setIsModalOpen(false);
    await generateRdDetails({
      variables: {
        rdProjectId: rdProject.id,
        fiscalYearId,
        input: {
          overrideProjectDescription: true,
          overrideTechnicalInNatureDetermination: false,
          overrideQualifiedPurposeDetermination: false,
        },
      },
      optimisticResponse: {
        generateRdDetails: {
          ...rdProject,
          processingState: ProcessingProjectState.InProgress,
        } satisfies RdProject,
      },
    });
  };

  const handleCancelUpdateNarrative = (): void => {
    setIsModalOpen(false);
  };

  const isUploading =
    loadingAddSupportingDocument || FileUploadsState.isUploading(rdProjectSupportingDocumentUploadTag);
  const isProcessingDocuments = rdProject.processingState === ProcessingProjectState.InProgress;
  const filesForUpload = FileUploadsState.getFilesWithUploadProgressForDisplaySection(
    rdProjectSupportingDocumentUploadTag,
  );
  const hasFileErrors = useMemo(() => filesForUpload.some((file) => file.status === 'error'), [filesForUpload]);

  return (
    <Stack as="form" direction="column" space={4}>
      {!isProcessingDocuments && (
        <SupportingDocumentsInputFormContainer
          label={
            <LabelWithHelp
              inputId={rdProjectSupportingDocumentAnalyticId}
              label="Supporting Documents"
              href={HELP_UPLOAD_RD_PROJECT_DOCUMENTS_URL}
            />
          }
          inputId={rdProjectSupportingDocumentAnalyticId}
        >
          <FileDropZone
            allowedMimeTypes={[FileMimeType.PDF, FileMimeType.TEXT_PLAIN, FileMimeType.WORD]}
            addFiles={addFiles}
            text="supporting documents"
            allowMultipleSelect
            disable={isUploading}
          />
          <FileDisplaySection files={filesForUpload} removeFile={FileUploadsState.removeFileUpload} />
          <Stack fullWidth>
            <SupportingDocumentsButton
              variant="primary"
              rounded="regular"
              size="base"
              disabled={isUploading || filesForUpload.length === 0 || hasFileErrors}
              onClick={uploadAllFiles}
            >
              Upload
            </SupportingDocumentsButton>
          </Stack>
        </SupportingDocumentsInputFormContainer>
      )}

      {rdProject.supportingDocuments.length > 0 && (
        <InputFormContainer label="Uploaded Documents" inputId={rdProjectRemoveSupportingDocumentAnalyticId}>
          <FileDisplaySection
            files={rdProject.supportingDocuments.map((file) => ({
              fileName: file.fileName,
              uploadPercent: 100,
              status: 'success',
              url: file.signedUrl,
            }))}
            removeFile={removeUploadedFile}
            disable={isUploading || isProcessingDocuments}
          />
          <Stack direction="column" space={2} fullWidth>
            <SupportingDocumentsButton
              variant="primary"
              rounded="regular"
              size="base"
              disabled={isUploading || isProcessingDocuments}
              onClick={handleOpenUpdateNarrativeModal}
            >
              Update Project Narrative
            </SupportingDocumentsButton>
            <GenerateNarrativeModal
              isOpen={isModalOpen}
              onConfirm={handleConfirmUpdateNarrative}
              onCancel={handleCancelUpdateNarrative}
            />
          </Stack>
        </InputFormContainer>
      )}
    </Stack>
  );
};
